import React, { memo, useRef } from 'react';
import { jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Portal from '@atlaskit/portal';
import { Popper } from '@atlaskit/popper';
import type { popoverWithRelay_hoverPopover_HoverPopoverWithRelayComponent$key } from '@atlassian/jira-relay/src/__generated__/popoverWithRelay_hoverPopover_HoverPopoverWithRelayComponent.graphql';
import { PopoverInternal } from '../popover-internal/index.tsx';
import type { HoverPopoverWithRelayProps } from './types.tsx';

const HoverPopoverWithRelayComponent = <T extends HTMLElement>({
	content,
	children,
	contentRef,
	entryPointActions,
	isInteractive,
	shouldGetParentRef,
	isFixedWidth,
	hidePopover,
	analyticsAttributes,
	fragmentRef,
}: HoverPopoverWithRelayProps<T>) => {
	const data = useFragment<popoverWithRelay_hoverPopover_HoverPopoverWithRelayComponent$key>(
		graphql`
			fragment popoverWithRelay_hoverPopover_HoverPopoverWithRelayComponent on JiraFieldHoverOrFocusState {
				isHoveredOrFocused
			}
		`,
		fragmentRef,
	);

	const triggerRef = useRef<T | null>(null);
	return (
		<>
			{children({ ref: triggerRef })}

			{data && 'isHoveredOrFocused' in data && data?.isHoveredOrFocused && triggerRef.current && (
				<Portal>
					<Popper placement="bottom-start" referenceElement={triggerRef.current}>
						{({ ref, style, update, isReferenceHidden }) => (
							<div
								ref={ref}
								// Style is required for react-popper to apply positioning correctly (allows it to render to parent)
								// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
								style={style}
							>
								<PopoverInternal
									content={content}
									contentRef={contentRef}
									entryPointActions={entryPointActions}
									isInteractive={isInteractive}
									shouldGetParentRef={shouldGetParentRef}
									isFixedWidth={isFixedWidth}
									hidePopover={hidePopover}
									analyticsAttributes={analyticsAttributes}
									onUpdate={update}
									isReferenceHidden={isReferenceHidden}
								/>
							</div>
						)}
					</Popper>
				</Portal>
			)}
		</>
	);
};

// @see https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087#issuecomment-656596623
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const HoverPopoverWithRelay = memo(
	HoverPopoverWithRelayComponent,
) as typeof HoverPopoverWithRelayComponent;
